import React from 'react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

/**
 * Hero Section Renderer
 * Variants: centered, split_image, video_bg, gradient
 */
export default function HeroRenderer({ props, variant }) {
  const {
    headline = 'Your Headline Here',
    subheadline = '',
    cta_text = 'Get Started',
    cta_url = '#',
    background_color = '#1c1917',
    text_color = '#ffffff',
    alignment = 'center',
    min_height = '75vh',
    image_url = '',
    video_url = '',
  } = props;

  const buttonStyle = {
    backgroundColor: text_color,
    color: background_color,
    fontFamily: BODY_FONT,
  };

  const headlineEl = (
    <h1
      className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight"
      style={{ fontFamily: HEADING_FONT, color: text_color }}
    >
      {headline}
    </h1>
  );

  const subheadlineEl = subheadline ? (
    <p
      className="text-lg sm:text-xl max-w-2xl opacity-80"
      style={{ fontFamily: BODY_FONT, color: text_color }}
    >
      {subheadline}
    </p>
  ) : null;

  const ctaEl = cta_text ? (
    <a
      href={cta_url || '#'}
      className="inline-block px-8 py-3.5 rounded-lg font-semibold text-sm sm:text-base transition-opacity hover:opacity-90"
      style={buttonStyle}
    >
      {cta_text}
    </a>
  ) : null;

  // Centered variant (default)
  if (variant === 'centered' || (!variant && !image_url)) {
    return (
      <section
        className="flex items-center justify-center px-6"
        style={{ backgroundColor: background_color, minHeight: min_height }}
      >
        <div
          className={`max-w-4xl w-full space-y-6 ${
            alignment === 'center' ? 'text-center mx-auto' : alignment === 'right' ? 'text-right ml-auto' : 'text-left'
          }`}
        >
          {headlineEl}
          {subheadlineEl && (
            <div className={alignment === 'center' ? 'flex justify-center' : ''}>
              {subheadlineEl}
            </div>
          )}
          {ctaEl}
        </div>
      </section>
    );
  }

  // Split image variant
  if (variant === 'split_image') {
    return (
      <section
        className="flex items-center px-6"
        style={{ backgroundColor: background_color, minHeight: min_height }}
      >
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-16">
          <div className="space-y-6">
            {headlineEl}
            {subheadlineEl}
            {ctaEl}
          </div>
          <div className="flex justify-center">
            {image_url ? (
              <img
                src={image_url}
                alt={headline}
                className="rounded-xl max-h-[500px] w-full object-cover shadow-2xl"
              />
            ) : (
              <div className="w-full aspect-[4/3] rounded-xl bg-white/10 flex items-center justify-center">
                <span className="text-sm opacity-40" style={{ color: text_color }}>
                  Add an image
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Video background variant
  if (variant === 'video_bg') {
    return (
      <section
        className="relative flex items-center justify-center px-6 overflow-hidden"
        style={{ backgroundColor: background_color, minHeight: min_height }}
      >
        {video_url && (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={video_url} type="video/mp4" />
          </video>
        )}
        <div className="absolute inset-0 bg-black/50" />
        <div
          className={`relative z-10 max-w-4xl w-full space-y-6 ${
            alignment === 'center' ? 'text-center mx-auto' : 'text-left'
          }`}
        >
          {headlineEl}
          {subheadlineEl && (
            <div className={alignment === 'center' ? 'flex justify-center' : ''}>
              {subheadlineEl}
            </div>
          )}
          {ctaEl}
        </div>
      </section>
    );
  }

  // Gradient variant
  if (variant === 'gradient') {
    return (
      <section
        className="flex items-center justify-center px-6"
        style={{
          background: `linear-gradient(135deg, ${background_color} 0%, ${background_color}cc 50%, ${background_color}88 100%)`,
          minHeight: min_height,
        }}
      >
        <div
          className={`max-w-4xl w-full space-y-6 ${
            alignment === 'center' ? 'text-center mx-auto' : 'text-left'
          }`}
        >
          {headlineEl}
          {subheadlineEl && (
            <div className={alignment === 'center' ? 'flex justify-center' : ''}>
              {subheadlineEl}
            </div>
          )}
          {ctaEl}
        </div>
      </section>
    );
  }

  // Fallback to centered
  return (
    <section
      className="flex items-center justify-center px-6"
      style={{ backgroundColor: background_color, minHeight: min_height }}
    >
      <div className="max-w-4xl w-full space-y-6 text-center mx-auto">
        {headlineEl}
        {subheadlineEl && <div className="flex justify-center">{subheadlineEl}</div>}
        {ctaEl}
      </div>
    </section>
  );
}
